/** Shared types and constants for the document preview tab. */

// Document type (camelCase only - consistent with types/redux.ts)
export interface DocumentData {
  docId?: string;
  originalFilename?: string;
  documentName?: string;
  documentCategory?: string;
  summaryText?: string;
  mimeType?: string;
  fileSize?: number;
  processingStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  lastUpdatedBy?: string;
  documentOrigin?: string;
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
}

export interface PreviewData {
  contentBlob: Blob;
  sasData?: {
    source?: string;
    sasUrl?: string;
    isSharePoint?: boolean;
  };
}

export interface ErrorData {
  message: string;
  type?: string;
  code?: string;
}

export interface FactData {
  factId?: string;
  documentId?: string;
  rationale?: string;
  factSourceType?: string;
  highlightContentString?: string;
  surroundingContentString?: string;
  factFromImage?: string;
  pageIndex?: number;
  pageIndices?: number[];
}

export interface DownloadProgress {
  isDownloading: boolean;
  loaded: number;
  total: number | null;
  percentage: number | null;
}

export const NO_DOWNLOAD_PROGRESS: DownloadProgress = {
  isDownloading: false,
  loaded: 0,
  total: null,
  percentage: null,
};

/** SharePoint failures we can give the user an actionable next step for. */
const SHAREPOINT_ERRORS: Record<string, ErrorData> = {
  SHAREPOINT_DENIED: {
    message:
      'Your SharePoint access was revoked. Please contact an administrator or open the file directly in SharePoint.',
    type: 'sharepoint_denied',
    code: 'SHAREPOINT_DENIED',
  },
  MISSING_IDENTIFIERS: {
    message:
      'SharePoint document identifiers are missing. Please refresh the project context and try again.',
    type: 'missing_identifiers',
    code: 'MISSING_IDENTIFIERS',
  },
};

/** Maps a structured SharePoint error code onto actionable messaging. */
export function sharePointError(err: unknown): ErrorData | null {
  const code = (err as { code?: string })?.code;
  return (code && SHAREPOINT_ERRORS[code]) || null;
}

/** Display name for a document, however it was labelled. */
export function filenameOf(document: DocumentData | null): string {
  return document?.originalFilename || document?.documentName || 'Unknown';
}

/** Doc-drafter generated files (outline, metadata, content) hide the edit controls. */
const GENERATED_DRAFT_FILES = ['draft_outline.yaml', 'draft_metadata.yaml', 'draft_content.yaml'];

export function isGeneratedDraftFile(document: DocumentData | null): boolean {
  const filename = filenameOf(document).toLowerCase();
  return GENERATED_DRAFT_FILES.some((name) => filename.includes(name));
}

/** Username is everything before the @ of the signed-in email. */
export function usernameFromEmail(email?: string): string {
  return (email || '').split('@')[0];
}
